import { useEffect, useMemo, useState } from 'react';
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Line,
  ComposedChart,
  Brush,
} from 'recharts';
import { Position } from './blacknScholes';
import { PositionMetaData } from '../OptionChain/types';
import CustomTooltip from './CustomTooltip/CustomToolTipPayoff';
import { generateDataPoint, generateData } from './utils';
import { useTheme } from '@mui/material';
import { FutureDataType } from 'types/ApiServicesTypes';
import React from 'react';
import { FiMaximize as _FiMaximize, FiMinimize as _FiMinimize } from 'react-icons/fi';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
const FiMaximize = _FiMaximize as React.FC<{ size?: number }>;
const FiMinimize = _FiMinimize as React.FC<{ size?: number }>;

const PayoffChart = ({
  positions,
  setMaxProfitLoss,
  date,
  lotSize,
  spot_price,
  futureData,
}: {
  positions: PositionMetaData[];
  date: string;
  setMaxProfitLoss: (value: {
    max_profit: string | number;
    max_loss: string | number;
    breakevenPoints: number[];
  }) => void;
  lotSize: number;
  spot_price: number | undefined;
  futureData: Record<string, FutureDataType>;
}) => {
  // Calculate strikes, min and max strike prices
  const strikes = positions.flatMap(p =>
    p.contractType === 'fut'
      ? Object.values(p.positionCandleWise).map(position =>
          Number(position.premium),
        )
      : Number(p.strike),
  );
  const minStrike = Math.min(...strikes) - 1500;
  const maxStrike = Math.max(...strikes) + 1500;
  const {
    component: {
      optionChainGraphColors: {
        areaNegative,
        areaPositive,
        cartesianGrid,
        line,
        yaxis,
      },
    },
  } = useTheme();

  // Generate spot prices
  const spot_prices = useMemo(
    () => generateDataPoint({ minStrike: minStrike, maxStrike: maxStrike }),
    [minStrike, maxStrike],
  );

  // Calculate expiry and real-time payoffs
  const {
    expiry_payoffs,
    real_time_payoffs,
  }: { expiry_payoffs: number[]; real_time_payoffs: number[] } = useMemo(() => {
    const expiry_payoffs = new Array(spot_prices.length).fill(0);
    const real_time_payoffs = new Array(spot_prices.length).fill(0);

    positions.forEach(pos_data => {
      Object.values(pos_data.positionCandleWise || {}).forEach(position => {
        const p = new Position({
          strike: pos_data.strike,
          contract_type: pos_data.contractType,
          direction: position.direction,
          premium: position.premium,
          quantity: Number(position.lots) * lotSize,
          entry_time: position.entryTime,
          expiry: pos_data.expiryDate,
          candle_time: date,
          risk_free_interest_rate: 0.070797, // default risk_free_interest_rate
          iv: Number(position.contractDetails.iv) / 100, // default iv
          spot_price_at_entry: 0,
          current_spot_price: spot_price || 1,
          current_contract_price:
            pos_data.contractType === 'fut'
              ? futureData[`${pos_data.strike}`]
                ? futureData[`${pos_data.strike}`].close
                : 1
              : 1,
        });
        const positionExpiryPayoffs = p.payoff(spot_prices);
        const positionRealTimePayoffs = p.real_time_payoff(spot_prices);

        positionExpiryPayoffs.forEach((payoff, idx) => {
          expiry_payoffs[idx] += payoff;
        });

        positionRealTimePayoffs.forEach((payoff, idx) => {
          real_time_payoffs[idx] += payoff;
        });
      });
    });

    return { expiry_payoffs, real_time_payoffs };
  }, [positions, spot_prices, date, lotSize, futureData, spot_price]);

  // Generate data
  const data = useMemo(
    () => generateData(expiry_payoffs, real_time_payoffs, minStrike, maxStrike),
    [expiry_payoffs, real_time_payoffs, minStrike, maxStrike],
  );

  // State to track brush range values
  const [brushRange, setBrushRange] = useState({
    start: data[0]?.underlyingPrice || 0,
    end: data[data.length - 1]?.underlyingPrice || 0,
  });

  // Helper function to round spot price to whole number (ignore decimals)
  const roundedSpotPrice = spot_price ? Math.round(spot_price) : 0;

  const { maxProfit, maxLoss } = useMemo(() => {
    let maxProfit = Math.max(...expiry_payoffs);
    let maxLoss = Math.min(...expiry_payoffs);

    if (data.length > 0) {
      const firstPayoff = data[0].payoff;
      const lastPayoff = data[data.length - 1].payoff;

      if (
        firstPayoff === maxProfit &&
        data.slice(1).every(point => point.payoff !== firstPayoff)
      ) {
        maxProfit = Infinity;
      }
      if (
        firstPayoff === maxLoss &&
        data.slice(1).every(point => point.payoff !== firstPayoff)
      ) {
        maxLoss = -Infinity;
      }

      if (
        lastPayoff === maxProfit &&
        data.slice(0, -1).every(point => point.payoff !== lastPayoff)
      ) {
        maxProfit = Infinity;
      }
      if (
        lastPayoff === maxLoss &&
        data.slice(0, -1).every(point => point.payoff !== lastPayoff)
      ) {
        maxLoss = -Infinity;
      }

      const maxPayoff = Math.max(...data.map(point => point.payoff));
      if (
        firstPayoff === lastPayoff &&
        firstPayoff === maxPayoff &&
        data.slice(1, -1).every(point => point.payoff !== firstPayoff)
      ) {
        maxProfit = Infinity;
      }

      const minPayoff = Math.min(...data.map(point => point.payoff));
      if (
        firstPayoff === lastPayoff &&
        firstPayoff === minPayoff &&
        data.slice(1, -1).every(point => point.payoff !== firstPayoff)
      ) {
        maxLoss = -Infinity;
      }
    }
    // Check if maxProfit and maxLoss are the same (with tolerance for floating-point precision)
    const tolerance = 1e-10;
    if (Math.abs(maxProfit - maxLoss) < tolerance) {
      if (maxProfit < 0) {
        maxProfit = 0;
      } else {
        maxLoss = 0;
      }
    }

    return { maxProfit, maxLoss };
  }, [expiry_payoffs, data]);

  const breakevenPoints = useMemo(() => {
    const points: number[] = [];
    for (let i = 1; i < data.length; i++) {
      const previousPayoff = data[i - 1].payoff;
      const currentPayoff = data[i].payoff;

      if (
        (previousPayoff > 0 && currentPayoff < 0) ||
        (previousPayoff < 0 && currentPayoff > 0)
      ) {
        points.push(data[i].underlyingPrice);
      }
    }
    return points;
  }, [data]);

  useEffect(() => {
    setMaxProfitLoss({
      max_loss: maxLoss,
      max_profit: maxProfit,
      breakevenPoints: breakevenPoints,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxLoss, maxProfit, setMaxProfitLoss]);

  // State for fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Handler to toggle fullscreen
  const handleOpenFullscreen = () => setIsFullscreen(true);
  const handleCloseFullscreen = () => setIsFullscreen(false);

  return (
    <>
      {/* Normal (non-fullscreen) chart with maximize button */}
      <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {!isFullscreen && (<IconButton
          onClick={handleOpenFullscreen}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 10000,
            // background: 'rgba(255,255,255,0.8)',
            border: 'none',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          aria-label="Enter Fullscreen"
        >
          <FiMaximize size={20} />
        </IconButton>)}
        {/* Chart content below (copied from previous return) */}
        <div style={{ flex: 1, position: 'relative' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={data}
              margin={{ top: 20, right: 10, left: 10, bottom: 10 }}
            >
              <CartesianGrid
                stroke={cartesianGrid.stroke}
                fill={cartesianGrid.fill}
                strokeWidth={2}
              />
              <XAxis
                dataKey="underlyingPrice"
                tick={{ fontFamily: 'Inter, sans-serif', fontWeight: '900' }}
              />
              <YAxis
                dataKey={'payoff'}
                tick={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: '900',
                  color: yaxis,
                }}
              />
              <Tooltip
                position={{ y: 70 }}
                isAnimationActive={false}
                cursor={{ stroke: yaxis, strokeWidth: 0.5, strokeDasharray: 6 }}
                content={<CustomTooltip active={false} label="" payload={[]} />}
              />
              <Area
                type="monotone"
                dataKey="positive"
                stroke={areaPositive.stroke}
                fill={areaPositive.fill}
                fillOpacity={0.5}
                strokeWidth={2.5}
              />
              <Area
                strokeWidth={2.5}
                type="monotone"
                dataKey="negative"
                stroke={areaNegative.stroke}
                fill={areaNegative.fill}
                fillOpacity={0.5}
              />
              <Line
                strokeWidth={2}
                type="monotone"
                strokeDasharray={4}
                strokeLinecap="round"
                dataKey="currentPayoff"
                stroke={line}
                dot={false}
              />
              <ReferenceLine y={0} stroke={yaxis} />
              {spot_price && (
                <ReferenceLine
                  x={roundedSpotPrice}
                  stroke="#ff7300"
                  strokeDasharray="3 3"
                  strokeWidth={2}
                  alwaysShow={true}
                  label={{
                    value: `Spot: ${spot_price?.toLocaleString()}`,
                    position: 'top',
                    fill: '#ff7300',
                    fontSize: 12,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 'bold',
                  }}
                />
              )}
              <Brush
                dataKey="underlyingPrice"
                height={30}
                stroke="#8884d8"
                onChange={brushData => {
                  if (
                    brushData &&
                    brushData.startIndex !== undefined &&
                    brushData.endIndex !== undefined
                  ) {
                    const startValue =
                      data[brushData.startIndex]?.underlyingPrice;
                    const endValue = data[brushData.endIndex]?.underlyingPrice;
                    if (startValue && endValue) {
                      setBrushRange({
                        start: startValue,
                        end: endValue,
                      });
                    }
                  }
                }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* Brush Range Display */}
        <div
          style={{
            height: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 10px',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
            color: yaxis,
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderTop: '1px solid rgba(0,0,0,0.1)',
          }}
        >
          <span>Start: {brushRange.start.toLocaleString()}</span>
          <span>End: {brushRange.end.toLocaleString()}</span>
        </div>
      </div>

      {/* Fullscreen Modal */}
      <Dialog
        open={isFullscreen}
        onClose={handleCloseFullscreen}
        maxWidth="xl"
        fullWidth
        // PaperProps={{ style: { background: '#fff' } }}
      >
        <div style={{ height: '70vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          <IconButton
            onClick={handleCloseFullscreen}
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 10000,
              // background: 'rgba(255,255,255,0.8)',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
            aria-label="Exit Fullscreen"
          >
            <FiMinimize size={20} />
          </IconButton>
          {/* Chart content below (same as above) */}
          <div style={{ flex: 1, position: 'relative' }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 20, right: 10, left: 10, bottom: 10 }}
              >
                <CartesianGrid
                  stroke={cartesianGrid.stroke}
                  fill={cartesianGrid.fill}
                  strokeWidth={2}
                />
                <XAxis
                  dataKey="underlyingPrice"
                  tick={{ fontFamily: 'Inter, sans-serif', fontWeight: '900' }}
                />
                <YAxis
                  dataKey={'payoff'}
                  tick={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: '900',
                    color: yaxis,
                  }}
                />
                <Tooltip
                  position={{ y: 70 }}
                  isAnimationActive={false}
                  cursor={{ stroke: yaxis, strokeWidth: 0.5, strokeDasharray: 6 }}
                  content={<CustomTooltip active={false} label="" payload={[]} />}
                />
                <Area
                  type="monotone"
                  dataKey="positive"
                  stroke={areaPositive.stroke}
                  fill={areaPositive.fill}
                  fillOpacity={0.5}
                  strokeWidth={2.5}
                />
                <Area
                  strokeWidth={2.5}
                  type="monotone"
                  dataKey="negative"
                  stroke={areaNegative.stroke}
                  fill={areaNegative.fill}
                  fillOpacity={0.5}
                />
                <Line
                  strokeWidth={2}
                  type="monotone"
                  strokeDasharray={4}
                  strokeLinecap="round"
                  dataKey="currentPayoff"
                  stroke={line}
                  dot={false}
                />
                <ReferenceLine y={0} stroke={yaxis} />
                {spot_price && (
                  <ReferenceLine
                    x={roundedSpotPrice}
                    stroke="#ff7300"
                    strokeDasharray="3 3"
                    strokeWidth={2}
                    alwaysShow={true}
                    label={{
                      value: `Spot: ${spot_price?.toLocaleString()}`,
                      position: 'top',
                      fill: '#ff7300',
                      fontSize: 12,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 'bold',
                    }}
                  />
                )}
                <Brush
                  dataKey="underlyingPrice"
                  height={30}
                  stroke="#8884d8"
                  onChange={brushData => {
                    if (
                      brushData &&
                      brushData.startIndex !== undefined &&
                      brushData.endIndex !== undefined
                    ) {
                      const startValue =
                        data[brushData.startIndex]?.underlyingPrice;
                      const endValue = data[brushData.endIndex]?.underlyingPrice;
                      if (startValue && endValue) {
                        setBrushRange({
                          start: startValue,
                          end: endValue,
                        });
                      }
                    }
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          {/* Brush Range Display */}
          <div
            style={{
              height: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 10px',
              fontSize: '12px',
              fontFamily: 'Inter, sans-serif',
              color: yaxis,
              backgroundColor: 'rgba(0,0,0,0.02)',
              borderTop: '1px solid rgba(0,0,0,0.1)',
            }}
          >
            <span>Start: {brushRange.start.toLocaleString()}</span>
            <span>End: {brushRange.end.toLocaleString()}</span>
          </div>
        </div>
      </Dialog>
    </>
  );
};
export default PayoffChart;
